import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Button, Combobox, Field, Input, Modal, type ComboboxOption } from '@/components/ui';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { ApiError, api } from '@/lib/api';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type {
  CompanyLink,
  CompanyLinkCreateInput,
  CompanyLinkKind,
  CompanyLinkUpdateInput,
} from '@/lib/types';

/** The server caps a link's label at 120 characters. */
const LINK_LABEL_MAX = 120;

/**
 * Mirrors the server's rule ("must start `http://` or `https://`"), case
 * insensitively so a pasted `HTTPS://` isn't refused for nothing. Empty passes
 * here — the URL is required, and that is said separately.
 *
 * ⚠️ Deliberately no length cap: a Drive/SharePoint link routinely runs past
 * 255 characters, and a guessed cap would silently truncate a valid link.
 *
 * Moved here from `CompanyDetailPage`, which no longer edits a URL column of
 * its own — this form is now the only place a proposal or report link is typed.
 */
function validateProposalUrl(next: string): string | null {
  if (next === '') return null;
  if (!/^https?:\/\//i.test(next)) {
    return 'Paste the full link — it has to start with http:// or https://.';
  }
  return null;
}

interface FormValues {
  label: string;
  url: string;
  cycleId: string;
}

type FieldKey = keyof FormValues;

export interface CompanyLinkFormModalProps {
  companyId: number;
  kind: CompanyLinkKind;
  /** How the block names this kind — "Proposal (Excel)". */
  kindLabel: string;
  /** The link being edited, or `null` to add one. */
  link: CompanyLink | null;
  /** The cycle a NEW link is written to. Fixed on create. */
  cycleId: number;
  cycleName: string;
  /** Offered on edit only, so a link filed under the wrong cycle can move. */
  cycleOptions: ComboboxOption[];
  onClose: () => void;
  /** Called with the cycle the link now sits on, so the caller can follow it. */
  onSaved: (cycleId: number) => void;
}

export function CompanyLinkFormModal({
  companyId,
  kind,
  kindLabel,
  link,
  cycleId,
  cycleName,
  cycleOptions,
  onClose,
  onSaved,
}: CompanyLinkFormModalProps) {
  const { ability } = useAuth();
  const formId = useId();
  const isEdit = link !== null;

  const initial: FormValues = {
    label: link?.label ?? '',
    url: link?.url ?? '',
    cycleId: String(link?.cycleId ?? cycleId),
  };
  const [values, setValues] = useState<FormValues>(initial);
  const [clientErrors, setClientErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [serverErrors, setServerErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // A save in flight when the dialog unmounts is abandoned, never applied to a
  // component that is gone.
  const controllerRef = useRef<AbortController | null>(null);
  useEffect(() => () => controllerRef.current?.abort(), []);

  const errors = { ...serverErrors, ...clientErrors };

  function setField(field: FieldKey, next: string) {
    setValues((prev) => ({ ...prev, [field]: next }));
    setClientErrors((prev) => {
      if (!(field in prev)) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    setServerErrors((prev) => {
      if (!(field in prev)) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  }

  const trimmedLabel = values.label.trim();
  const trimmedUrl = values.url.trim();

  /** Only the keys that moved — an untouched key is never re-asserted. */
  function buildPatch(): CompanyLinkUpdateInput {
    const patch: CompanyLinkUpdateInput = {};
    if (!link) return patch;
    if (trimmedUrl !== link.url) patch.url = trimmedUrl;
    const storedLabel = link.label ?? '';
    // `null`, never `''`, is how a label is cleared.
    if (trimmedLabel !== storedLabel) patch.label = trimmedLabel === '' ? null : trimmedLabel;
    const nextCycle = Number(values.cycleId);
    if (Number.isInteger(nextCycle) && nextCycle > 0 && nextCycle !== link.cycleId) {
      patch.cycleId = nextCycle;
    }
    return patch;
  }

  function buildCreate(): CompanyLinkCreateInput {
    return {
      cycleId,
      kind,
      url: trimmedUrl,
      ...(trimmedLabel === '' ? {} : { label: trimmedLabel }),
    };
  }

  function validate(): Partial<Record<FieldKey, string>> {
    const next: Partial<Record<FieldKey, string>> = {};
    if (trimmedUrl === '') next.url = 'Paste the link.';
    else {
      const scheme = validateProposalUrl(trimmedUrl);
      if (scheme) next.url = scheme;
    }
    if (trimmedLabel.length > LINK_LABEL_MAX) {
      next.label = `Keep the label to ${LINK_LABEL_MAX} characters.`;
    }
    if (isEdit && values.cycleId === '') next.cycleId = 'Choose a cycle.';
    return next;
  }

  const patchSize = isEdit ? Object.keys(buildPatch()).length : 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const clientSide = validate();
    setClientErrors(clientSide);
    if (Object.keys(clientSide).length > 0) {
      setFormError(null);
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setSubmitting(true);
    setFormError(null);
    setServerErrors({});
    try {
      if (link) {
        const patch = buildPatch();
        const saved = await api.updateCompanyLink(companyId, link.id, patch, controller.signal);
        if (controller.signal.aborted) return;
        onSaved(saved.data.cycleId);
      } else {
        const saved = await api.createCompanyLink(companyId, buildCreate(), controller.signal);
        if (controller.signal.aborted) return;
        onSaved(saved.data.cycleId);
      }
      onClose();
    } catch (caught) {
      if (controller.signal.aborted) return;
      applyFailure(caught);
    } finally {
      if (!controller.signal.aborted) setSubmitting(false);
    }
  }

  function applyFailure(caught: unknown) {
    if (!(caught instanceof ApiError)) {
      setFormError('Couldn’t reach the server. Nothing was saved.');
      return;
    }
    // 2026-10-09: a company the reader can see but does not own is a 403
    // COMPANY_NOT_OWNED naming the rule; render the server's sentence.
    if (caught.code === 'COMPANY_NOT_OWNED') {
      setFormError(`${caught.message} Nothing was saved.`);
      return;
    }
    if (caught.status === 403) {
      setFormError(
        `${
          denialReason(ability, 'update', 'companies') ??
          'You no longer have permission to change this account.'
        } Nothing was saved.`,
      );
      return;
    }
    if (caught.status === 404) {
      setFormError(
        isEdit
          ? 'This link, or the account it belongs to, no longer exists. Nothing was saved.'
          : 'This account or that cycle no longer exists. Nothing was saved.',
      );
      return;
    }
    // `cycleId` is a control only on edit; on create it is fixed, so a refusal
    // naming it belongs in the banner rather than on a field nobody can see.
    const known: FieldKey[] = isEdit ? ['label', 'url', 'cycleId'] : ['label', 'url'];
    const routed = caught.routeFieldErrors(known);
    setServerErrors(routed.fields as Partial<Record<FieldKey, string>>);
    const banner = [...routed.unroutable, ...caught.formMessages];
    if (banner.length > 0) setFormError(banner.join(' '));
    else if (Object.keys(routed.fields).length === 0) setFormError(caught.message);
    else setFormError(null);
  }

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = submitting || (isEdit && patchSize === 0);
  const dirty =
    values.label !== initial.label || values.url !== initial.url || values.cycleId !== initial.cycleId;

  const title = isEdit ? `Edit ${kindLabel} link` : `Add ${kindLabel} link — ${cycleName}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      dismissOnBackdrop={!dirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Add link'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="grid gap-4" noValidate>
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <Field label="URL" required error={errors.url}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type="url"
              inputMode="url"
              value={values.url}
              onChange={(e) => setField('url', e.target.value)}
              autoComplete="off"
              spellCheck={false}
              invalid={Boolean(errors.url)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Label" error={errors.label}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              value={values.label}
              onChange={(e) => setField('label', e.target.value)}
              maxLength={LINK_LABEL_MAX}
              autoComplete="off"
              invalid={Boolean(errors.label)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        {isEdit && (
          <Field label="Cycle" required error={errors.cycleId}>
            {({ id }) => (
              <Combobox
                id={id}
                value={values.cycleId}
                options={cycleOptions}
                placeholder="Choose a cycle"
                searchPlaceholder="Search cycles…"
                invalid={Boolean(errors.cycleId)}
                onChange={(next) => {
                  if (next === '') return;
                  setField('cycleId', next);
                }}
              />
            )}
          </Field>
        )}
      </form>
    </Modal>
  );
}
